import { useState } from "react";
import { FolderTree, Plus, Trash2, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import {
  useAddGroupMember,
  useCreateGroup,
  useDeleteGroup,
  useGroups,
  useRemoveGroupMember,
} from "@/hooks/usePlanner";

export default function PlannerGroupsPage() {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const clients = workspaces.filter((w) => w.website);

  const { data: groups = [] } = useGroups();
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();

  const [name, setName] = useState("");

  const add = () => {
    if (!name.trim()) return;
    createGroup.mutate({ name: name.trim() }, { onSuccess: () => setName("") });
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <FolderTree className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Client Groups</h1>
          <p className="text-sm text-muted-foreground">Group clients for cross-client rollups and reporting.</p>
        </div>
      </div>

      {/* Create group */}
      <Card className="border-0 shadow-sm">
        <div className="flex flex-wrap items-end gap-2 px-4 py-3">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">New group</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="e.g. Home Services — Northeast"
              className="h-9 min-w-[240px]"
            />
          </div>
          <Button onClick={add} disabled={!name.trim() || createGroup.isPending} className="h-9">
            <Plus className="mr-1 h-4 w-4" /> Create group
          </Button>
        </div>
      </Card>

      {groups.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No groups yet — create one above.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((g) => {
            const available = clients.filter((c) => !g.members.includes(c.website as string));
            return (
              <Card key={g.id} className="border-0 shadow-sm">
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                  <h2 className="font-semibold text-foreground">{g.name}</h2>
                  <span className="text-xs text-muted-foreground">{g.members.length} clients</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteGroup.mutate(g.id)}
                    aria-label="Delete group"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 px-4 py-3">
                  {g.members.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No clients yet.</span>
                  ) : (
                    g.members.map((t) => (
                      <Badge key={t} variant="outline" className="gap-1 border-border font-normal">
                        {t}
                        <button
                          type="button"
                          onClick={() => removeMember.mutate({ id: g.id, tenantId: t })}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${t}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
                <div className="border-t border-border px-4 py-3">
                  <Select
                    value={undefined}
                    onValueChange={(t) => addMember.mutate({ id: g.id, tenantId: t })}
                  >
                    <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Add a client…" /></SelectTrigger>
                    <SelectContent>
                      {available.length === 0 ? (
                        <SelectItem value="__none" disabled>All clients added</SelectItem>
                      ) : (
                        available.map((c) => (
                          <SelectItem key={c.website} value={c.website as string}>{c.name} · {c.website}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
